import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Journal from "./pages/Journal";
import Checklist from "./pages/Checklist";
import Coach from "./pages/Coach";
import Education from "./pages/Education";
import Pricing from "./pages/Pricing";
import Connections from "./pages/Connections";
import FreeTier from "./pages/FreeTier";
import Admin from "./pages/Admin";
import QuarterToClose from "./pages/QuarterToClose";

const ADMIN_EMAILS = ["investolife2019@gmail.com", "tara.muhammad@southredford.org"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Check if user has active subscription
export const useSubscription = () => {
  const { user } = useAuth();
  const isActive = user?.subscription_status === "active";
  const tier = user?.subscription_tier || "free";
  return { isActive, tier, isPremium: isActive && tier !== "free" };
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="font-mono text-blue-500 animate-pulse">LOADING...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// Premium Route - requires active subscription
const PremiumRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isPremium = user?.subscription_status === "active";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="font-mono text-blue-500 animate-pulse">LOADING...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!isPremium) {
    return <Navigate to="/learn" replace />;
  }

  return children;
};

// Layout with Sidebar
const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPremium = user?.subscription_status === "active";
  const isAdmin = ADMIN_EMAILS.includes(user?.email);

  const navItems = isPremium ? [
    { path: "/dashboard", label: "DASHBOARD", icon: "chart" },
    { path: "/journal", label: "TRADE JOURNAL", icon: "book" },
    { path: "/checklist", label: "SNIPER CHECKLIST", icon: "check" },
    { path: "/quarter-to-close", label: "QUARTER TO CLOSE", icon: "trophy" },
    { path: "/coach", label: "COACH BASHIRAH", icon: "message" },
    { path: "/education", label: "PLAYBOOK", icon: "graduation" },
    { path: "/connections", label: "CONNECTIONS", icon: "link" },
    ...(isAdmin ? [{ path: "/admin", label: "ADMIN PANEL", icon: "admin", highlight: true }] : []),
  ] : [
    { path: "/learn", label: "FREE LESSONS", icon: "graduation" },
    { path: "/pricing", label: "UPGRADE", icon: "star", highlight: true },
    ...(isAdmin ? [{ path: "/admin", label: "ADMIN PANEL", icon: "admin" }] : []),
  ];

  const getIcon = (icon) => {
    const icons = {
      chart: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      ),
      book: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      check: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      message: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      graduation: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
        </svg>
      ),
      star: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      ),
      link: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      admin: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      trophy: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
    };
    return icons[icon];
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#050505] border-r border-[#262626] flex flex-col h-screen fixed left-0 top-0 z-50">
        {/* Logo */}
        <div className="p-6 border-b border-[#262626]">
          <p className="text-[10px] font-mono text-slate-600 tracking-widest mb-1">A QUARTER TO CLOSE PRODUCT</p>
          <h1 className="font-mono text-xl font-bold tracking-tight">
            <span className="text-blue-500">INSTITUTIONAL</span>
            <span className="text-slate-400"> EDGE</span>
          </h1>
          <p className="text-xs text-slate-600 mt-1 font-mono">BEAT THE RIGGED GAME</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm font-mono border-l-2 transition-colors ${
                location.pathname === item.path
                  ? "text-white bg-[#171717] border-blue-500"
                  : item.highlight
                  ? "text-yellow-500 border-yellow-500/50 bg-yellow-500/5 hover:bg-yellow-500/10"
                  : "text-slate-400 border-transparent hover:text-white hover:bg-[#171717] hover:border-blue-500/50"
              }`}
            >
              {getIcon(item.icon)}
              {item.label}
              {item.highlight && (
                <span className="ml-auto text-xs bg-yellow-500 text-black px-1.5 py-0.5 font-bold">NEW</span>
              )}
            </button>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-[#262626]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-sm bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
              <span className="text-blue-500 font-mono text-sm">{user?.name?.[0]?.toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate font-mono">{user?.name}</p>
              <p className={`text-xs font-mono ${isPremium ? "text-emerald-500" : "text-yellow-500"}`}>
                {isPremium ? `${user?.subscription_tier?.toUpperCase()} MEMBER` : "FREE TIER"}
              </p>
            </div>
          </div>
          {!isPremium && (
            <button
              onClick={() => navigate("/pricing")}
              className="w-full text-xs font-mono text-yellow-500 py-2 border border-yellow-500/30 bg-yellow-500/5 hover:bg-yellow-500/10 mb-2"
            >
              UPGRADE NOW
            </button>
          )}
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full text-xs font-mono text-slate-500 hover:text-red-400 transition-colors py-2 border border-[#262626] hover:border-red-900/50"
          >
            LOGOUT
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 min-h-screen">
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route
            path="/learn"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <FreeTier />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PremiumRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </PremiumRoute>
            }
          />
          <Route
            path="/journal"
            element={
              <PremiumRoute>
                <AppLayout>
                  <Journal />
                </AppLayout>
              </PremiumRoute>
            }
          />
          <Route
            path="/checklist"
            element={
              <PremiumRoute>
                <AppLayout>
                  <Checklist />
                </AppLayout>
              </PremiumRoute>
            }
          />
          <Route
            path="/coach"
            element={
              <PremiumRoute>
                <AppLayout>
                  <Coach />
                </AppLayout>
              </PremiumRoute>
            }
          />
          <Route
            path="/education"
            element={
              <PremiumRoute>
                <AppLayout>
                  <Education />
                </AppLayout>
              </PremiumRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Pricing />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/connections"
            element={
              <PremiumRoute>
                <AppLayout>
                  <Connections />
                </AppLayout>
              </PremiumRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Admin />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quarter-to-close"
            element={
              <PremiumRoute>
                <AppLayout>
                  <QuarterToClose />
                </AppLayout>
              </PremiumRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" theme="dark" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
